import {
  LBGraph,
  LBWorkflow,
  LBNode as ProtoLBNode,
  LBEdge as ProtoLBEdge,
  LBNodeData,
  WorkflowTestRunData,
} from '@/proto/logiblocks/logiblocks';
import {
  OutputType,
  type LBNodeDataWithIndexer,
  type ReactFlowEdge,
  type ReactFlowNode,
} from '@/pages/Logiblocks/types';
import { LBEDGE_MARKER_END } from './styles';
import { getLBNodeInfo } from './nodes/registry';

// ============================================================================
// Serialization: React Flow Types -> Protobuf
// ============================================================================

/**
 * Converts a React Flow node to a protobuf LBNode message.
 */
export function nodeToProto(node: ReactFlowNode): ProtoLBNode {
  return {
    id: node.id,
    data: node.data as LBNodeData,
  };
}

/**
 * Converts a React Flow edge to a protobuf LBEdge message
 */
function edgeToProto(edge: ReactFlowEdge): ProtoLBEdge {
  const edgeOrder = edge.data?.edgeOrder;
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    source_handle: edge.sourceHandle || undefined,
    target_handle: edge.targetHandle || undefined,
    edge_order: edgeOrder,
  };
}

/**
 * Serializes a ReactFlow workflow to protobuf binary format as LBWorkflow.
 *
 * @param nodes - Array of React Flow nodes
 * @param edges - Array of React Flow edges
 * @returns Uint8Array containing the serialized protobuf data
 */
export function serializeWorkflow(
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): Uint8Array {
  const workflow = reactFlowToLBWorkflow(nodes, edges);
  return LBWorkflow.encode(withWorkflowFieldDefaults(workflow)).finish();
}

/**
 * Returns the node with any scalar field that was added to its type after its data
 * was built (older in-memory data, fixtures, copied nodes) set to its default. The
 * encoder only skips a scalar equal to its default, so `undefined` would crash it.
 * Copies instead of mutating: node data can be frozen.
 */
function withNodeFieldDefaults(node: ProtoLBNode): ProtoLBNode {
  const data = node.data;
  const aiCall = data?.ai_call_action;
  if (
    !data ||
    !aiCall ||
    (aiCall.reply_wait_seconds !== undefined && aiCall.dont_wait !== undefined)
  ) {
    return node;
  }
  return {
    ...node,
    data: {
      ...data,
      ai_call_action: {
        ...aiCall,
        reply_wait_seconds: aiCall.reply_wait_seconds ?? 0,
        dont_wait: aiCall.dont_wait ?? false,
      },
    },
  };
}

function withWorkflowFieldDefaults(workflow: LBWorkflow): LBWorkflow {
  return {
    ...workflow,
    graphs: workflow.graphs.map((graph) => ({
      ...graph,
      nodes: graph.nodes.map(withNodeFieldDefaults),
    })),
  };
}

// ============================================================================
// Deserialization: Protobuf -> React Flow Types
// ============================================================================

/**
 * Converts a protobuf LBNode message to a React Flow node.
 * Position is set to a placeholder - calculateGraphLayout() computes actual positions.
 */
function nodeFromProto(protoLBNode: ProtoLBNode): ReactFlowNode {
  if (!protoLBNode.data) {
    throw new Error(`Invalid protobuf LBNode: missing data`);
  }

  // Shallow freeze to catch in-place mutation of node.data.
  return {
    id: protoLBNode.id,
    type: 'lbNode',
    position: { x: 0, y: 0 },
    data: Object.freeze(protoLBNode.data) as LBNodeDataWithIndexer,
  };
}

/**
 * Converts a protobuf LBEdge message to a React Flow edge
 */
function edgeFromProto(protoLBEdge: ProtoLBEdge): ReactFlowEdge {
  return {
    id: protoLBEdge.id,
    source: protoLBEdge.source,
    target: protoLBEdge.target,
    sourceHandle: protoLBEdge.source_handle || undefined,
    targetHandle: protoLBEdge.target_handle || undefined,
    type: 'lbEdge',
    markerEnd: LBEDGE_MARKER_END,
    // Freeze to catch in-place mutation of edge.data.
    data: Object.freeze({ edgeOrder: protoLBEdge.edge_order ?? 0 }),
  };
}

/**
 * Deserializes protobuf binary data (LBWorkflow) back to ReactFlow.
 *
 * @param data - Uint8Array containing the serialized LBWorkflow protobuf data
 * @returns Object containing nodes and edges arrays with parentId preserved
 * @throws Error if the data is invalid or cannot be parsed
 */
export function deserializeWorkflow(data: Uint8Array): {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
} {
  try {
    const workflow = LBWorkflow.decode(data);
    return lbWorkflowToReactFlow(workflow);
  } catch (error) {
    throw new Error(
      `Failed to deserialize graph: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Converts an LBWorkflow to React Flow nodes and edges.
 * Flattens multi-graph workflows into a single graph with parentId relationships.
 */
export function lbWorkflowToReactFlow(workflow: LBWorkflow): {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
} {
  const rootGraph = workflow.graphs[0];
  if (!rootGraph) return { nodes: [], edges: [] };

  const subgraphMappings = buildSubgraphIndexMappings(workflow);
  const resultNodes: ReactFlowNode[] = [];
  const resultEdges: ReactFlowEdge[] = rootGraph.edges.map(edgeFromProto);

  for (const protoNode of rootGraph.nodes) {
    resultNodes.push(nodeFromProto(protoNode));

    // Check if this node has a subgraph
    const subgraphIndex = subgraphMappings[protoNode.id];
    if (subgraphIndex !== undefined) {
      const nested = flattenSubgraph(
        workflow,
        subgraphIndex,
        protoNode.id,
        subgraphMappings
      );
      resultNodes.push(...nested.nodes);
      resultEdges.push(...nested.edges);
    }
  }

  return { nodes: resultNodes, edges: resultEdges };
}

/**
 * Converts a flattened ReactFlow graph back to an LBWorkflow with multiple graphs.
 * Nodes with parentId are moved into subgraphs owned by their parent nodes.
 */
export function reactFlowToLBWorkflow(
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): LBWorkflow {
  // Serialization performs in-place mutations on subgraph parents (e.g.
  // setSubgraphIndex writes nodeData.<variant>.subgraphIndex). Clone the
  // inputs up front so the caller's nodes[]/edges[] are never touched.
  nodes = structuredClone(nodes);
  edges = structuredClone(edges);

  // Separate root graph nodes from subgraph nodes
  const rootGraphNodes: ReactFlowNode[] = [];
  const subgraphNodesByParent = new Map<string, ReactFlowNode[]>();

  for (const node of nodes) {
    if (node.parentId) {
      const siblings = subgraphNodesByParent.get(node.parentId) ?? [];
      siblings.push(node);
      subgraphNodesByParent.set(node.parentId, siblings);
    } else {
      rootGraphNodes.push(node);
    }
  }

  // Build graphs array: index 0 = root, index 1+ = subgraphs
  const graphs: LBGraph[] = [{ nodes: [], edges: [] }];
  const parentIdToGraphIndex = new Map<string, number>();

  // Find ALL SUBGRAPH_PARENT nodes (not just those with children).
  // This includes nested subgraph parents (which have a parentId themselves).
  // Sort by existing index to preserve relative order, then reassign
  // sequential indices to compact any holes from deleted subgraphs.
  // (Subgraph index "holes" can occur when an older subgraph is deleted after a newer subgraph is created.)
  const subgraphParents = nodes
    .filter((node) => {
      const nodeInfo = node.data
        ? getLBNodeInfo(node.data as LBNodeData)
        : undefined;
      return nodeInfo?.outputType === OutputType.SUBGRAPH_PARENT;
    })
    .map((node) => {
      const nodeInfo = getLBNodeInfo(node.data as LBNodeData);
      const existingIndex =
        nodeInfo?.getSubgraphIndex?.(node.data as LBNodeData) ?? 0;
      return { node, existingIndex };
    })
    .sort((a, b) => a.existingIndex - b.existingIndex);

  // Assign sequential indices (compacts any holes)
  for (const { node } of subgraphParents) {
    const graphIndex = graphs.length;
    parentIdToGraphIndex.set(node.id, graphIndex);
    graphs.push({ nodes: [], edges: [] });
  }

  // Sanity check: ensure all nodes with parentId have a SUBGRAPH_PARENT parent.
  // In normal operation this should never fail - insertNewChild only sets parentId
  // when the parent is a SUBGRAPH_PARENT. But if data is corrupted or manually
  // constructed incorrectly, this gives a clear error rather than crashing later.
  for (const parentId of Array.from(subgraphNodesByParent.keys())) {
    if (!parentIdToGraphIndex.has(parentId)) {
      throw new Error(
        `Node "${parentId}" has child nodes but no setSubgraphIndex defined`
      );
    }
  }

  // Populate root graph
  const rootGraphNodeIds = new Set(
    rootGraphNodes.map((n: ReactFlowNode) => n.id)
  );
  for (const node of rootGraphNodes) {
    // Update subgraph index on parent nodes
    const graphIndex = parentIdToGraphIndex.get(node.id);
    if (graphIndex !== undefined) {
      const nodeInfo = node.data
        ? getLBNodeInfo(node.data as LBNodeData)
        : undefined;
      if (!nodeInfo?.setSubgraphIndex) {
        throw new Error(
          `Node "${node.id}" has child nodes but no setSubgraphIndex defined`
        );
      }
      nodeInfo.setSubgraphIndex(node.data as LBNodeData, graphIndex);
    }
    graphs[0].nodes.push(nodeToProto(node));
  }
  for (const edge of edges) {
    if (
      rootGraphNodeIds.has(edge.source) &&
      rootGraphNodeIds.has(edge.target)
    ) {
      graphs[0].edges.push(edgeToProto(edge));
    }
  }

  // Populate subgraphs
  for (const [parentId, subgraphNodes] of Array.from(subgraphNodesByParent)) {
    const graphIndex = parentIdToGraphIndex.get(parentId)!;
    const subgraphNodeIds = new Set(
      subgraphNodes.map((n: ReactFlowNode) => n.id)
    );

    for (const subgraphNode of subgraphNodes) {
      // Update subgraph index if this node is also a parent of a nested subgraph
      const nestedGraphIndex = parentIdToGraphIndex.get(subgraphNode.id);
      if (nestedGraphIndex !== undefined) {
        const nodeInfo = subgraphNode.data
          ? getLBNodeInfo(subgraphNode.data as LBNodeData)
          : undefined;
        if (!nodeInfo?.setSubgraphIndex) {
          throw new Error(
            `Node "${subgraphNode.id}" has child nodes but no setSubgraphIndex defined`
          );
        }
        nodeInfo.setSubgraphIndex(
          subgraphNode.data as LBNodeData,
          nestedGraphIndex
        );
      }
      graphs[graphIndex].nodes.push(nodeToProto(subgraphNode));
    }

    for (const edge of edges) {
      if (
        subgraphNodeIds.has(edge.source) &&
        subgraphNodeIds.has(edge.target)
      ) {
        graphs[graphIndex].edges.push(edgeToProto(edge));
      }
    }
  }

  return { graphs };
}

/**
 * Maps SUBGRAPH_PARENT node IDs to the subgraph indices they should use
 * in the protobuf representation of the workflow.
 *
 * Validates that:
 * - All subgraph indices are > 0 (index 0 is always the root graph)
 * - No two nodes point to the same subgraph index
 *
 * @throws Error if validation fails
 */
function buildSubgraphIndexMappings(
  workflow: LBWorkflow
): Record<string, number> {
  const mappings: Record<string, number> = {};
  const usedIndices = new Map<number, string>(); // index -> nodeId that uses it

  for (const graph of workflow.graphs) {
    for (const node of graph.nodes) {
      if (!node.data) continue;

      const nodeInfo = getLBNodeInfo(node.data);
      if (
        nodeInfo?.outputType === OutputType.SUBGRAPH_PARENT &&
        nodeInfo.getSubgraphIndex
      ) {
        const subgraphIndex = nodeInfo.getSubgraphIndex(node.data);
        if (subgraphIndex === undefined) continue;

        // Validate index > 0
        if (subgraphIndex <= 0) {
          throw new Error(
            `Invalid subgraph index ${subgraphIndex} for node "${node.id}": must be > 0`
          );
        }

        // Validate no duplicate indices
        const existingNodeId = usedIndices.get(subgraphIndex);
        if (existingNodeId) {
          throw new Error(
            `Duplicate subgraph index ${subgraphIndex}: used by both "${existingNodeId}" and "${node.id}"`
          );
        }

        usedIndices.set(subgraphIndex, node.id);
        mappings[node.id] = subgraphIndex;
      }
    }
  }

  return mappings;
}

/**
 * Recursively flattens a subgraph into ReactFlow nodes with parentId relationships.
 *
 * LBWorkflow stores loops as separate graphs (graphs[0] = root, graphs[1+] = subgraphs).
 * ReactFlow needs all nodes in a single flat array, using parentId to establish
 * subgraph/subgraphParent relationships. This function bridges these two representations.
 */
function flattenSubgraph(
  workflow: LBWorkflow,
  graphIndex: number,
  parentId: string,
  subgraphMappings: Record<string, number>
): { nodes: ReactFlowNode[]; edges: ReactFlowEdge[] } {
  const graph = workflow.graphs[graphIndex];
  if (!graph) return { nodes: [], edges: [] };

  const resultNodes: ReactFlowNode[] = [];
  const resultEdges: ReactFlowEdge[] = graph.edges.map(edgeFromProto);

  for (const protoNode of graph.nodes) {
    const node = nodeFromProto(protoNode);
    node.parentId = parentId;
    node.extent = 'parent';
    resultNodes.push(node);

    // Check if this node has its own subgraph (nested loop)
    const nestedIndex = subgraphMappings[protoNode.id];
    if (nestedIndex !== undefined) {
      const nested = flattenSubgraph(
        workflow,
        nestedIndex,
        protoNode.id,
        subgraphMappings
      );
      resultNodes.push(...nested.nodes);
      resultEdges.push(...nested.edges);
    }
  }

  return { nodes: resultNodes, edges: resultEdges };
}

/**
 * Serializes a single LBNodeData to raw protobuf bytes. Combine
 * with `bytesEqual` to detect whether two LBNodeDatas are equal.
 */
export function lbNodeDataToBytes(data: LBNodeData): Uint8Array {
  // Normalise via a JSON round-trip before encoding.
  // - toJSON handles undefined repeated fields safely (uses `?.length` guard).
  // - fromJSON uses `Array.isArray` checks and falls back to [] for any field
  //   that is missing OR is a non-array (e.g. a scalar stored where an array
  //   is expected).
  // This guards against data persisted before a proto schema change and against
  // form state that accidentally coerces an array to a scalar.
  const normalized = LBNodeData.fromJSON(LBNodeData.toJSON(data));
  return LBNodeData.encode(normalized).finish();
}

/**
 * Byte-level equality for two Uint8Arrays.
 */
export function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Serializes an LBWorkflow to a base64-encoded string for API transport.
 */
export function lbWorkflowToBase64(workflow: LBWorkflow): string {
  try {
    // Normalize TeamNotifRecipients in email/notification nodes:
    // Old saved data may lack the new additionalRecipients field, causing
    // the protobuf encoder to crash on `for (const v of undefined)`.
    for (const graph of workflow.graphs) {
      for (const node of graph.nodes) {
        const emailAction = node.data?.email_action;
        if (emailAction) {
          for (const field of ['to', 'cc', 'bcc'] as const) {
            const recipients = emailAction[field];
            if (recipients) {
              for (const r of recipients) {
                if (!r.additional_recipients) {
                  r.additional_recipients = [];
                }
              }
            }
          }
        }
      }
    }
    const bytes = LBWorkflow.encode(
      withWorkflowFieldDefaults(workflow)
    ).finish();
    return btoa(String.fromCharCode(...Array.from(bytes)));
  } catch (error) {
    throw new Error(
      `Failed to serialize LBWorkflow: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Serializes a single LBNode to a base64-encoded string for API transport.
 */
export function lbNodeToBase64(node: ProtoLBNode): string {
  try {
    const bytes = ProtoLBNode.encode(withNodeFieldDefaults(node)).finish();
    return btoa(String.fromCharCode(...Array.from(bytes)));
  } catch (error) {
    throw new Error(
      `Failed to serialize LBNode: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Serializes a WorkflowTestRunData to a base64-encoded string for API transport.
 */
export function testRunDataToBase64(testRunData: WorkflowTestRunData): string {
  try {
    const bytes = WorkflowTestRunData.encode(testRunData).finish();
    return btoa(String.fromCharCode(...Array.from(bytes)));
  } catch (error) {
    throw new Error(
      `Failed to serialize WorkflowTestRunData: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Decodes a base64-encoded string to a message of the proto type `messageFns` decodes.
 */
export function protoFromBase64<T>(
  base64String: string,
  messageFns: { decode(input: Uint8Array): T }
): T {
  const binaryString = atob(base64String);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return messageFns.decode(bytes);
}

/**
 * Deserializes a base64-encoded string to an LBWorkflow.
 */
export function lbWorkflowFromBase64(base64String: string): LBWorkflow {
  try {
    return protoFromBase64(base64String, LBWorkflow);
  } catch (error) {
    throw new Error(
      `Failed to deserialize LBWorkflow: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}
